/**
 * _lmShared — atoms reused across the 12 Lead-Manager screens.
 *
 * Apr 28, 2026 (Squad-D). Pure UI helpers. All inline-styled with the
 * existing AURA token system (--ink-*, --ivory-*, --saffron, --forest, etc.).
 *
 * Shared types match Squad-A's evolving backend contract for Lead /
 * LeadApprovalRequest. Optional fields mean "graceful degrade if absent."
 */

import type { CSSProperties, ReactNode } from 'react';

/* ─── Shared types ─────────────────────────────────────────────────────── */

export interface Lead {
  id: string;
  // Customer identity
  name?: string | null;
  customerName?: string | null;
  contactPhone?: string | null;
  phone?: string | null;
  email?: string | null;
  // Source & state
  source?: string | null;
  verificationStatus?: string | null;
  conversationState?: string | null;
  conversationStateUpdatedAt?: string | null;
  status?: string | null;
  priority?: 'hot' | 'warm' | 'cold' | string | null;
  // Product / region / context
  productInterest?: string | null;
  product?: string | null;
  region?: string | null;
  city?: string | null;
  state?: string | null;
  budget?: string | null;
  budgetMin?: number | null;
  budgetMax?: number | null;
  notes?: string | null;
  note?: string | null;
  // Assignment
  teamId?: string | null;
  teamName?: string | null;
  assigneeName?: string | null;
  assigneeId?: string | null;
  // Timing
  createdAt?: string | null;
  verifiedAt?: string | null;
  verifierName?: string | null;
}

export interface Team {
  id: string;
  name: string;
  headName?: string | null;
  headId?: string | null;
  memberCount?: number | null;
  region?: string | null;
  openLeads?: number | null;
  weeklyConversion?: number | null;
}

export interface ApprovalRequest {
  id: string;
  leadId?: string | null;
  leadName?: string | null;
  fromUserName?: string | null;
  fromTeamName?: string | null;
  toUserName?: string | null;
  toTeamName?: string | null;
  reason?: string | null;
  status?: 'pending' | 'approved' | 'rejected' | string | null;
  createdAt?: string | null;
  reviewerName?: string | null;
  slaRemainingMinutes?: number | null;
  stake?: string | null;
}

/* ─── Source palette ───────────────────────────────────────────────────── */

export interface SourceMeta {
  label: string;
  hint: string;
  color: string;
}

export const SOURCES: Record<string, SourceMeta> = {
  chatkart: { label: 'ChatKart', hint: 'WhatsApp', color: '#25D366' },
  whatsapp: { label: 'WhatsApp', hint: 'WhatsApp', color: '#25D366' },
  indiamart: { label: 'IndiaMART', hint: 'B2B form', color: '#F47216' },
  justdial: { label: 'JustDial', hint: 'Listing', color: '#D4282E' },
  web: { label: 'Website', hint: 'Form', color: '#3A6A8C' },
  website: { label: 'Website', hint: 'Form', color: '#3A6A8C' },
  field: { label: 'Field exec', hint: 'Manual', color: '#8B4A6B' },
  referral: { label: 'Referral', hint: 'Word-of-mouth', color: '#2F7A5B' },
  unknown: { label: 'Other', hint: 'Source unknown', color: '#8B8373' },
};

export function sourceMeta(src: string | null | undefined): SourceMeta {
  if (!src) return SOURCES.unknown;
  const k = String(src).toLowerCase();
  return SOURCES[k] ?? SOURCES.unknown;
}

/* ─── Display helpers ──────────────────────────────────────────────────── */

export function leadName(l: Lead): string {
  return l.customerName || l.name || 'Unknown lead';
}

export function leadPhone(l: Lead): string {
  return l.contactPhone || l.phone || '—';
}

export function leadProduct(l: Lead): string {
  return l.productInterest || l.product || '—';
}

export function leadRegion(l: Lead): string {
  return l.region || l.city || l.state || '—';
}

export function relativeTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const diffMs = Date.now() - t;
  const m = Math.round(diffMs / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.round(h / 24)}d`;
}

export function priorityFrom(l: Lead): 'hot' | 'warm' | 'cold' {
  const p = (l.priority ?? '').toLowerCase();
  if (p === 'hot' || p === 'warm' || p === 'cold') return p;
  return 'warm';
}

/* ─── Atoms ────────────────────────────────────────────────────────────── */

export function SourceIcon({
  source,
  size = 18,
}: {
  source: string | null | undefined;
  size?: number;
}) {
  const s = sourceMeta(source);
  return (
    <div
      title={s.label}
      style={{
        width: size,
        height: size,
        borderRadius: 4,
        background: s.color,
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: Math.max(8, size * 0.5),
        fontWeight: 700,
        flexShrink: 0,
        fontFamily: 'var(--ff-mono)',
      }}
    >
      {s.label[0]}
    </div>
  );
}

export function PriorityDot({ p }: { p: 'hot' | 'warm' | 'cold' }) {
  const c = p === 'hot' ? '#B14141' : p === 'warm' ? '#C98A3B' : '#8B8373';
  return (
    <span
      title={p}
      style={{
        display: 'inline-block',
        width: 6,
        height: 6,
        borderRadius: 3,
        background: c,
        flexShrink: 0,
      }}
    />
  );
}

export function PhoneNum({ n }: { n: string }) {
  return (
    <span
      className="mono"
      style={{
        fontSize: 12,
        color: 'var(--ink-2)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {n}
    </span>
  );
}

export function KbdHint({ k }: { k: string }) {
  return (
    <span
      className="mono"
      style={{
        fontSize: 10,
        color: 'var(--ink-3)',
        background: 'var(--ivory-2)',
        padding: '1px 5px',
        borderRadius: 3,
        border: '1px solid var(--line)',
      }}
    >
      {k}
    </span>
  );
}

export function StatusChip({ status }: { status: string | null | undefined }) {
  const map: Record<string, { label: string; tone: '' | 'amber' | 'red' | 'green' | 'blue' }> = {
    pending: { label: 'Pending', tone: 'amber' },
    pending_verification: { label: 'Pending', tone: 'amber' },
    chatkart: { label: 'ChatKart verifying', tone: '' },
    chatkart_verifying: { label: 'ChatKart verifying', tone: '' },
    manual: { label: 'Needs manual', tone: 'red' },
    needs_manual: { label: 'Needs manual', tone: 'red' },
    verified: { label: 'Verified', tone: 'green' },
    assigned: { label: 'Assigned', tone: 'blue' },
    rejected: { label: 'Rejected', tone: 'red' },
    snoozed: { label: 'Snoozed', tone: '' },
    approved: { label: 'Approved', tone: 'green' },
  };
  const s = (status ?? 'pending').toLowerCase();
  const m = map[s] ?? { label: s, tone: '' as const };
  return (
    <span className={`chip ${m.tone ? 'chip-' + m.tone : ''}`}>{m.label}</span>
  );
}

export function PriorityChip({ p }: { p: 'hot' | 'warm' | 'cold' }) {
  if (p === 'hot') return <span className="chip chip-red"><PriorityDot p="hot" /> Hot</span>;
  if (p === 'warm') return <span className="chip chip-amber"><PriorityDot p="warm" /> Warm</span>;
  return <span className="chip"><PriorityDot p="cold" /> Cold</span>;
}

export function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        color: 'var(--ink-3)',
        textTransform: 'uppercase',
        letterSpacing: 0.06,
        fontWeight: 500,
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}

export function Avatar({
  name,
  size = 28,
  bg = 'var(--ivory-2)',
}: {
  name: string;
  size?: number;
  bg?: string;
}) {
  const initials = (name ?? '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: bg,
        color: 'var(--ink-1)',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'var(--ff-serif)',
        fontWeight: 500,
        fontSize: Math.max(10, size * 0.4),
        flexShrink: 0,
        border: '1px solid var(--line)',
      }}
    >
      {initials || '?'}
    </div>
  );
}

export function Panel({
  title,
  sub,
  children,
  action,
  noPad,
}: {
  title?: string;
  sub?: string;
  children: ReactNode;
  action?: ReactNode;
  noPad?: boolean;
}) {
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      {(title || action) && (
        <div
          style={{
            padding: '12px 14px',
            borderBottom: '1px solid var(--line)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ fontWeight: 500, fontSize: 13 }}>{title}</div>
            {sub && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{sub}</div>}
          </div>
          <div style={{ flex: 1 }} />
          {action}
        </div>
      )}
      <div style={{ padding: noPad ? 0 : 14 }}>{children}</div>
    </div>
  );
}

export function KpiCard({
  label,
  value,
  delta,
  sub,
  tone,
}: {
  label: string;
  value: string | number;
  delta?: string;
  sub?: string;
  tone?: 'up' | 'warn';
}) {
  const dColor = tone === 'up' ? 'var(--forest-600)' : tone === 'warn' ? 'var(--warn)' : 'var(--ink-3)';
  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="kpi-label" style={{ marginBottom: 6 }}>
        {label}
      </div>
      <div className="num-display" style={{ fontSize: 28, lineHeight: 1 }}>
        {value === '' || value == null ? '—' : value}
      </div>
      {delta && (
        <div style={{ fontSize: 11, color: dColor, marginTop: 5, fontWeight: 500 }}>{delta}</div>
      )}
      {sub && <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  sub,
}: {
  icon?: ReactNode;
  title: string;
  sub?: string;
}) {
  return (
    <div style={{ padding: 36, textAlign: 'center' }}>
      {icon && <div style={{ marginBottom: 8, color: 'var(--ink-3)' }}>{icon}</div>}
      <div style={{ fontSize: 13, color: 'var(--ink-2)', fontWeight: 500, marginBottom: 4 }}>
        {title}
      </div>
      {sub && (
        <div style={{ fontSize: 12, color: 'var(--ink-3)', maxWidth: 360, margin: '0 auto' }}>
          {sub}
        </div>
      )}
    </div>
  );
}

export function PageShell({
  title,
  sub,
  right,
  children,
  scroll = true,
}: {
  title: string;
  sub?: string;
  right?: ReactNode;
  children: ReactNode;
  scroll?: boolean;
}) {
  return (
    <div className="aura-root" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 'calc(100vh - 56px)', background: 'var(--ivory-0)' }}>
      <div
        style={{
          padding: '16px 24px',
          background: 'var(--ivory-0)',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexShrink: 0,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--ff-serif)',
              fontWeight: 500,
              fontSize: 22,
              color: 'var(--ink-0)',
              letterSpacing: -0.3,
            }}
          >
            {title}
          </div>
          {sub && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{sub}</div>}
        </div>
        {right && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{right}</div>}
      </div>
      <div style={{ flex: 1, overflowY: scroll ? 'auto' : 'visible', padding: scroll ? 24 : 0 }}>
        {children}
      </div>
    </div>
  );
}

/* ─── Sub-nav for /lead-manager/* ─────────────────────────────────────── */

export interface LMSubNavItem {
  key: string;
  label: string;
  to: string;
  count?: number;
}

export function LMSubNav({ items, activeKey }: { items: LMSubNavItem[]; activeKey: string }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 0,
        borderBottom: '1px solid var(--line)',
        background: 'var(--ivory-0)',
        padding: '0 24px',
        flexWrap: 'wrap',
      }}
    >
      {items.map((it) => {
        const active = it.key === activeKey;
        const styleObj: CSSProperties = {
          border: 'none',
          background: 'transparent',
          padding: '12px 14px',
          fontSize: 13,
          color: active ? 'var(--ink-0)' : 'var(--ink-3)',
          fontWeight: active ? 500 : 400,
          borderBottom: active ? '2px solid var(--ink-0)' : '2px solid transparent',
          cursor: 'pointer',
          marginBottom: -1,
          whiteSpace: 'nowrap',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
        };
        return (
          <a key={it.key} href={it.to} style={styleObj}>
            {it.label}
            {it.count !== undefined && (
              <span
                className="num"
                style={{
                  fontSize: 11,
                  color: 'var(--ink-3)',
                  background: 'var(--ivory-2)',
                  padding: '1px 6px',
                  borderRadius: 8,
                }}
              >
                {it.count}
              </span>
            )}
          </a>
        );
      })}
    </div>
  );
}

/* ─── Constants from Sun Stellar context ──────────────────────────────── */

export const PRODUCT_OPTIONS = ['SS Tank', 'Solar WH', 'Heat Pump', 'SS Plumbing', 'Other'];
export const REGION_OPTIONS = [
  'Bengaluru Urban',
  'Mysuru',
  'Hubli-Dharwad',
  'Mangaluru',
  'Belagavi',
  'Hosur',
  'Tumakuru',
];

/* ─── List unwrap helper ──────────────────────────────────────────────── */

export function unwrapList<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (raw && typeof raw === 'object') {
    const obj = raw as { items?: T[]; data?: T[] };
    if (Array.isArray(obj.items)) return obj.items;
    if (Array.isArray(obj.data)) return obj.data;
  }
  return [];
}
